import React, { FC, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import tinyColor from 'tinycolor2'
import { StepTemplate } from '../../components/styled/stepTemplate/StepTemplate'
import { useReportViewEvent } from '../../hooks'
import { reportTrackingEvent } from '../../tracking/thunks'
import { ReduxState } from '../../store/reducers'
import { Progress } from '../../@types/progress'
import { ExtendedBuyOverviewStepIcon } from '../../components/icons/StepIcons/ExtendedBuyOverviewStepIcon'
import './ExtendedBuyerLeadOverviewStep.scss'
import Location from '../../components/Location/Location'
import LocationRadius from '../../components/LocationRadius/LocationRadius'
import Area from '../../components/Area/Area'
import RoomsStepper from '../../components/RoomsStepper/RoomsStepper'
import TextInputArea from '../../components/TextInputArea/TextInputArea'
import { Button } from '../../components/styled/CosmaButton/CosmaButton'
import { IntegrationType } from '../../@types/config'
import { RealEstateType } from '../../@types/realEstateType'
import { updateLocationRange } from '../../store/location/locationSlice'
import { updateTextInputArea } from '../../store/lead/leadSlice'

export interface ExtendedBuyerLeadOverviewProps {
  goToNextStep: () => unknown
  goToPreviousStep?: () => unknown
  progress: Progress
  displayBack: boolean
  goToErrorStep: () => unknown
}

const HEADLINE = 'Bitte überprüfe deine Angaben'
export const PAGE_TITLE = 'buyer-overview'
export const ExtendedBuyerLeadOverviewStep: FC<ExtendedBuyerLeadOverviewProps> = (props) => {
  useReportViewEvent({ pageTitle: PAGE_TITLE })

  const {
    config: { theme, integrationType },
    lead: { realEstateType, livingSpace, rooms, plotArea },
    location: { locationRange, data },
  } = useSelector((state: ReduxState) => state)

  const dispatch = useDispatch()
  const [isLocationValid, setIsLocationValid] = useState<boolean>(true)
  const [isRadiusValid, setIsRadiusValid] = useState<boolean>(true)
  const [location, setLocation] = useState<string>(data?.postcode ? data?.postcode : '')
  const [locationRadius, setLocationRadius] = useState<number>(locationRange ? locationRange : 0)
  const [livingSpaceValue, setLivingSpace] = useState<number>(livingSpace ? livingSpace : 0)
  const [roomsValue, setRooms] = useState<number>(rooms ? rooms : 0)
  const [plotAreaValue, setPlotArea] = useState<number>(plotArea ? plotArea : 0)

  const externalIntegration = integrationType === IntegrationType.EXTERNAL
  const isIv24Integration = integrationType === IntegrationType.IMMOVERKAUF24

  const onLocationValidationChanged = (isValid: boolean, value?: string) => {
    setIsLocationValid(isValid)
    if (value) setLocation(value)
  }

  const onLocationRadiusChange = (value?: number, isValid?: boolean) => {
    if (isValid && typeof value === 'number') {
      dispatch(updateLocationRange(value))
      setLocationRadius(value)
    }
    setIsRadiusValid(isValid ?? false)
  }

  const onRoomChange = (value?: number) => {
    if (value) setRooms(value)
  }

  const onLivingSpaceChanged = (value?: number) => {
    if (value) setLivingSpace(value)
  }

  const onPlotAreaChanged = (value?: number) => {
    if (value) setPlotArea(value)
  }

  const reportLocationEvent = (value?: string) => {
    dispatch(
      reportTrackingEvent({
        pageTitle: PAGE_TITLE,
        label: 'postcode_submit',
        eventName: 'lead_input',
        inputValue: value ? value : '',
        customDimensions: { ga_cd_lead_engine_input_value: value ? value : '' },
        action: 'realtor-leadengine',
        category: 'residential',
        flow: '',
      })
    )
  }

  const reportRoomEvent = (value?: number) => {
    dispatch(
      reportTrackingEvent({
        pageTitle: PAGE_TITLE,
        category: 'residential',
        action: 'realtor-leadengine',
        label: `rooms_submit`,
        inputValue: `${value}`,
        eventName: 'lead_input',
        customDimensions: {
          ga_cd_lead_engine_input_value: value,
        },
      })
    )
  }

  const reportLocationRadiusEvent = (locationRadius?: number) => {
    dispatch(
      reportTrackingEvent({
        pageTitle: PAGE_TITLE,
        category: 'residential',
        action: 'realtor-leadengine',
        label: 'radius_submit',
        eventName: 'lead_input',
        flow: 'IBW',
        inputValue: locationRadius?.toString() ?? '',
        customDimensions: { ga_cd_lead_engine_input_value: locationRadius },
      })
    )
  }
  const reportLivingSpaceEvent = (value?: number) => {
    dispatch(
      reportTrackingEvent({
        pageTitle: PAGE_TITLE,
        category: 'residential',
        action: 'realtor-leadengine',
        label: `${realEstateType === RealEstateType.APARTMENT ? `apartmentspace_submit` : `housespace_submit`}`,
        inputValue: `${value}`,
        eventName: 'lead_input',
        customDimensions: {
          ga_cd_lead_engine_input_value: value,
        },
      })
    )
  }

  const reportPlotAreaEvent = (value?: number) => {
    dispatch(
      reportTrackingEvent({
        pageTitle: PAGE_TITLE,
        category: 'residential',
        action: 'realtor-leadengine',
        label: `propertyspace_submit`,
        inputValue: `${value}`,
        eventName: 'lead_input',
        customDimensions: {
          ga_cd_lead_engine_input_value: value,
        },
      })
    )
  }

  const onTextInputAreaChange = (value?: string) => {
    if (value) dispatch(updateTextInputArea(value))
  }

  const onNextClick = () => {
    dispatch(
      reportTrackingEvent({
        pageTitle: 'buyer-overview',
        category: 'residential',
        action: 'realtor-leadengine',
        label: 'overview_submit',
        inputValue: ``,
        eventName: 'lead_input',
        customDimensions: {
          ga_cd_lead_engine_input_value: ``,
        },
      })
    )
    reportLocationEvent(location)
    reportLocationRadiusEvent(locationRadius)
    reportLivingSpaceEvent(livingSpaceValue)
    reportRoomEvent(roomsValue)
    if (plotAreaValue != 0) reportPlotAreaEvent(plotAreaValue)
    props.goToNextStep()
  }
  const isCtaButtonEnabled = isLocationValid && isRadiusValid

  return (
    <StepTemplate
      icon={<ExtendedBuyOverviewStepIcon />}
      headline={HEADLINE}
      goToPreviousStep={props.goToPreviousStep}
      progress={props.progress}
      backgroundColor={theme.background}
      textColor={theme.text}
      displayBack={props.displayBack}
      displayNext={false}
    >
      <div className='overview-container' data-testid='overview-container'>
        <div className='grid-item' data-testid='location-component'>
          <Location onValidationChange={onLocationValidationChanged} onError={props.goToErrorStep} autofocus={false} />
        </div>
        <div className='grid-item margin-top-l '>
          <LocationRadius initialValue={locationRange} onChange={onLocationRadiusChange} />
        </div>
        <div className='grid-item margin-top-l '>
          <Area
            onLivingSpaceChanged={onLivingSpaceChanged}
            onPlotAreaChanged={onPlotAreaChanged}
            className={'area-container-component'}
          />
        </div>
        {realEstateType != RealEstateType.LAND ? (
          <div className='grid-item margin-top-l '>
            <RoomsStepper onChange={onRoomChange} className={'rooms-container-component'} />
          </div>
        ) : null}

        <div className='grid-item margin-top-l '>
          <TextInputArea onChange={onTextInputAreaChange} />
        </div>
        <div className='grid-item margin-top-xl align-center'>
          <Button
            appearance='primary'
            onClick={onNextClick}
            data-testid='overview-next-button'
            disabled={!isCtaButtonEnabled}
            primary={theme.primary}
            secondary={theme.secondary}
            textColor={
              externalIntegration && tinyColor(theme?.secondary).isDark()
                ? '#FFFFFF'
                : isIv24Integration && tinyColor(theme?.primary).isDark()
                ? '#FFFFFF'
                : theme.text
            }
            secondaryLight={theme.secondaryLight}
            iv24BackgroundColor={isIv24Integration ? theme.secondary : theme.background}
          >
            {'Eingaben bestätigen'}
          </Button>
        </div>
      </div>
    </StepTemplate>
  )
}
export default ExtendedBuyerLeadOverviewStep
